import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, Timestamp, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Loader2, Search, User } from 'lucide-react';
import { format, addMinutes, isBefore, isAfter, startOfDay, setHours, setMinutes } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { createAppointment } from '@/lib/firebase/queries/appointments';
import { useLanguage } from '@/contexts/LanguageContext';

// Utility function to convert Western numerals to Arabic-Indic numerals
const toArabicNumerals = (str: string) => {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return str.replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
};

interface ManualSchedulingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCoachId?: string; // When provided, hides coach selection and uses this coach
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: Array<{
    role: string;
    isActive: boolean;
    assignedAt: Date;
    assignedBy: string;
  }>;
  role: string;
}

interface TimeSlot {
  date: Date;
  coachId: string;
  availabilityId: string;
}

const SESSION_TIMES = {
  session1: { start: { hours: 17, minutes: 0 }, end: { hours: 20, minutes: 0 } },
  session2: { start: { hours: 20, minutes: 0 }, end: { hours: 22, minutes: 0 } }
};

const APPOINTMENT_DURATION = 15; // minutes

function generateTimeSlots(
  startDate: Date,
  endDate: Date,
  selectedDays: { [key: string]: string[] }
): Date[] {
  const slots: Date[] = [];
  const now = new Date();

  // Skip if availability is in the past
  if (isAfter(now, endDate)) return slots;
  
  // Start from today or availability start date, whichever is later
  let currentDate = startOfDay(isBefore(startDate, now) ? now : startDate);
  
  while (isBefore(currentDate, endDate)) {
    const dayOfWeek = currentDate.getDay().toString();
    const selectedSessions = selectedDays[dayOfWeek] || [];

    for (const session of selectedSessions) {
      const sessionTimes = SESSION_TIMES[session as keyof typeof SESSION_TIMES];
      if (!sessionTimes) continue;

      let slotTime = new Date(currentDate);
      slotTime = setHours(slotTime, sessionTimes.start.hours);
      slotTime = setMinutes(slotTime, sessionTimes.start.minutes);
      
      const sessionEnd = new Date(currentDate);
      sessionEnd.setHours(sessionTimes.end.hours, sessionTimes.end.minutes, 0, 0);

      // Generate slots for this session
      while (isBefore(slotTime, sessionEnd)) {
        if (isAfter(slotTime, now)) {
          slots.push(new Date(slotTime));
        }
        slotTime = addMinutes(slotTime, APPOINTMENT_DURATION);
      }
    }

    currentDate = addMinutes(currentDate, 24 * 60); // Next day
  }

  return slots.sort((a, b) => a.getTime() - b.getTime());
}

export function ManualSchedulingDialog({ open, onOpenChange, currentCoachId }: ManualSchedulingDialogProps) {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<User[]>([]);
  const [coaches, setCoaches] = useState<User[]>([]);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const { language, t, dir } = useLanguage();
  const dateLocale = language === 'ar' ? ar : enUS;
  
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [selectedCoach, setSelectedCoach] = useState<string>(currentCoachId || '');
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  
  // Customer search states
  const [customerSearchQuery, setCustomerSearchQuery] = useState<string>('');
  const [customerSearchResults, setCustomerSearchResults] = useState<User[]>([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomerData, setSelectedCustomerData] = useState<User | null>(null);

  // Debug state changes
  console.log('🎯 [ManualScheduling] State Debug:', {
    open,
    currentCoachId,
    selectedCoach,
    selectedCustomer,
    selectedSlot,
    availableSlotsCount: availableSlots.length,
    loading,
    shouldShowTimeSlots: !!(selectedCoach || currentCoachId)
  });

  useEffect(() => {
    if (open) {
      fetchUsers();
      setSelectedCustomer('');
      setSelectedCoach(currentCoachId || '');
      setSelectedSlot('');
      setAvailableSlots([]);
      // Reset customer search
      setCustomerSearchQuery('');
      setCustomerSearchResults([]);
      setSelectedCustomerData(null);
      
      // If currentCoachId is provided (coach dashboard), fetch slots immediately
      if (currentCoachId) {
        fetchAvailableSlots(currentCoachId);
      }
    }
  }, [open, currentCoachId]);

  useEffect(() => {
    if (selectedCoach) {
      fetchAvailableSlots(selectedCoach);
    } else {
      setAvailableSlots([]);
    }
  }, [selectedCoach]);

  // Trigger customer search when query changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchCustomers(customerSearchQuery);
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [customerSearchQuery]);

  const fetchUsers = async () => {
    try {
      // Get all users first
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);

      // Filter customers and coaches based on their roles
      const allUsers = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          firstName: data.firstNameEn || data.firstName,
          lastName: data.lastNameEn || data.lastName,
          email: data.email,
          roles: data.roles || [],
          role: data.role // Include the old role field for backward compatibility
        } as User;
      });

      const customers = allUsers.filter(user => 
        // Check both new roles array and old role field
        (user.roles.some(role => role.role === 'customer' && role.isActive)) ||
        user.role === 'customer'
      );

      const coaches = allUsers.filter(user => 
        // Check both new roles array and old role field
        (user.roles.some(role => role.role === 'coach' && role.isActive)) ||
        user.role === 'coach'
      );

      console.log('Found customers:', customers.length);
      console.log('Found coaches:', coaches.length);

      setCustomers(customers);
      setCoaches(coaches);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    }
  };

  const searchCustomers = async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setCustomerSearchResults([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      
      // Search in users collection for customers - NO orderBy to avoid missing users without firstNameEn
      const usersQuery = query(
        collection(db, 'users'),
        limit(500) // Increased limit since we can't use orderBy
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      const allUsers = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        firstName: doc.data().firstNameEn || doc.data().firstName,
        lastName: doc.data().lastNameEn || doc.data().lastName,
      }));

      // Filter for customers and search criteria
      const searchLower = searchQuery.toLowerCase();
      const filteredCustomers = allUsers.filter(user => {
        const roles = user.roles || [];
        const isCustomer = roles.some((r: any) => r.role === 'customer' && r.isActive) || user.role === 'customer';
        
        if (!isCustomer) return false;

        const firstNameEn = (user.firstNameEn || user.firstName || '').toLowerCase();
        const lastNameEn = (user.lastNameEn || user.lastName || '').toLowerCase();
        const firstNameAr = (user.firstNameAr || '').toLowerCase();
        const lastNameAr = (user.lastNameAr || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const phone = (user.phone || '').toLowerCase();

        return firstNameEn.includes(searchLower) ||
               lastNameEn.includes(searchLower) ||
               firstNameAr.includes(searchLower) ||
               lastNameAr.includes(searchLower) ||
               email.includes(searchLower) ||
               phone.includes(searchLower) ||
               `${firstNameEn} ${lastNameEn}`.includes(searchLower);
      });

      // Sort by name client-side
      filteredCustomers.sort((a, b) => {
        const nameA = `${a.firstNameEn || a.firstName || ''} ${a.lastNameEn || a.lastName || ''}`.trim().toLowerCase();
        const nameB = `${b.firstNameEn || b.firstName || ''} ${b.lastNameEn || b.lastName || ''}`.trim().toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setCustomerSearchResults(filteredCustomers.slice(0, 10)); // Limit to 10 results
    } catch (error) {
      console.error('Error searching customers:', error);
      toast.error(t('dashboard.admin.manualScheduling.searchError'));
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const handleCustomerSelect = (customer: User) => {
    setSelectedCustomer(customer.id);
    setSelectedCustomerData(customer);
    const displayName = language === 'ar' 
      ? `${customer.firstNameAr || customer.firstName || ''} ${customer.lastNameAr || customer.lastName || ''}`.trim()
      : `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
    setCustomerSearchQuery(displayName);
    setCustomerSearchResults([]);
  };

  const fetchAvailableSlots = async (coachId: string) => {
    try {
      setLoading(true);
      
      console.log('🔍 [ManualScheduling] Fetching slots for coach:', coachId);
      
      // Get approved availabilities that are not held
      const availabilitiesQuery = query(
        collection(db, 'availabilities'),
        where('coachId', '==', coachId),
        where('status', '==', 'approved')
      );
      
      // Get existing appointments
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('coachId', '==', coachId),
        where('date', '>=', Timestamp.fromDate(new Date()))
      );

      const [availabilitiesSnapshot, appointmentsSnapshot] = await Promise.all([
        getDocs(availabilitiesQuery),
        getDocs(appointmentsQuery)
      ]);

      console.log('📅 [ManualScheduling] Found availabilities:', availabilitiesSnapshot.docs.length);
      console.log('📋 [ManualScheduling] Found appointments:', appointmentsSnapshot.docs.length);

      // Get all booked slots
      const bookedSlots = appointmentsSnapshot.docs.map(doc => 
        doc.data().date.toDate().getTime()
      );

      const slots: TimeSlot[] = [];

      // Generate available slots for each availability
      availabilitiesSnapshot.docs.forEach(doc => {
        const availability = { id: doc.id, ...doc.data() };
        
        console.log('🔍 [ManualScheduling] Processing availability:', {
          id: doc.id,
          status: availability.status,
          isHeld: availability.isHeld,
          startDate: availability.startDate.toDate(),
          endDate: availability.endDate.toDate(),
          selectedDays: availability.selectedDays
        });

        // Skip if held
        if (availability.isHeld) {
          console.log('⏸️ [ManualScheduling] Skipping held availability:', doc.id);
          return;
        }

        const availabilitySlots = generateTimeSlots(
          availability.startDate.toDate(),
          availability.endDate.toDate(),
          availability.selectedDays || {}
        );

        console.log('🎯 [ManualScheduling] Generated slots for availability:', availabilitySlots.length);

        // Filter out booked slots
        const availableSlots = availabilitySlots.filter(slot => 
          !bookedSlots.includes(slot.getTime())
        );

        console.log('✅ [ManualScheduling] Available slots after filtering:', availableSlots.length);

        slots.push(...availableSlots.map(date => ({
          date,
          coachId,
          availabilityId: doc.id
        })));
      });

      console.log('🎉 [ManualScheduling] Total available slots:', slots.length);
      const sortedSlots = slots.sort((a, b) => a.date.getTime() - b.date.getTime());
      console.log('📝 [ManualScheduling] Setting availableSlots state with:', sortedSlots.length, 'slots');
      setAvailableSlots(sortedSlots);
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Failed to fetch available slots');
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async () => {
    if (!selectedCustomer || !selectedCoach || !selectedSlot) {
      toast.error('Please select all required fields');
      return;
    }

    try {
      setLoading(true);
      const slot = availableSlots.find(s => s.date.getTime() === parseInt(selectedSlot));
      if (!slot) throw new Error('Invalid slot selected');

      // Double-check that the slot is still available
      const doubleCheckQuery = query(
        collection(db, 'appointments'),
        where('coachId', '==', selectedCoach),
        where('date', '==', Timestamp.fromDate(slot.date))
      );
      
      const doubleCheckSnapshot = await getDocs(doubleCheckQuery);
      
      if (!doubleCheckSnapshot.empty) {
        toast.error('This slot has just been booked. Please select another time.');
        await fetchAvailableSlots(selectedCoach);
        return;
      }

      // Get customer data for phone number and name
      const customerData = selectedCustomerData || customers.find(c => c.id === selectedCustomer);

      await createAppointment({
        customerId: selectedCustomer,
        coachId: selectedCoach,
        date: slot.date,
        status: 'scheduled',
        customerPhone: customerData?.phone || '',
        customerName: customerData ? (
          language === 'ar' 
            ? `${customerData.firstNameAr || customerData.firstName || ''} ${customerData.lastNameAr || customerData.lastName || ''}`.trim()
            : `${customerData.firstName || ''} ${customerData.lastName || ''}`.trim()
        ) : '',
        customerNameEn: customerData ? `${customerData.firstName || ''} ${customerData.lastName || ''}`.trim() : '',
        customerNameAr: customerData ? `${customerData.firstNameAr || ''} ${customerData.lastNameAr || ''}`.trim() : ''
      });

      toast.success(t('dashboard.admin.manualScheduling.success'));
      onOpenChange(false);
    } catch (error) {
      console.error('Error scheduling appointment:', error);
      if (error instanceof Error && error.message.includes('scheduled availability times')) {
        toast.error(
          language === 'ar' 
            ? 'يمكن حجز المواعيد فقط خلال أوقات التوفر المجدولة'
            : 'Appointments can only be booked during scheduled availability times'
        );
      } else {
        toast.error(t('dashboard.admin.manualScheduling.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" dir={dir}>
        <DialogHeader className={dir === 'rtl' ? 'text-right' : 'text-left'}>
          <DialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            {t('dashboard.admin.manualScheduling.title')}
          </DialogTitle>
          <DialogDescription className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            {currentCoachId 
              ? t('dashboard.admin.manualScheduling.descriptionCoach')
              : t('dashboard.admin.manualScheduling.description')
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
              <Label className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.admin.manualScheduling.customer')}</Label>
              <div className="relative">
                <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground ${dir === 'rtl' ? 'right-3' : 'left-3'}`} />
                <Input
                  placeholder={selectedCustomerData ? '' : t('dashboard.admin.manualScheduling.searchCustomer')}
                  value={selectedCustomerData 
                    ? (language === 'ar' 
                        ? `${selectedCustomerData.firstNameAr || selectedCustomerData.firstName || ''} ${selectedCustomerData.lastNameAr || selectedCustomerData.lastName || ''}`.trim()
                        : `${selectedCustomerData.firstName || ''} ${selectedCustomerData.lastName || ''}`.trim()
                      )
                    : customerSearchQuery
                  }
                  onChange={(e) => {
                    if (selectedCustomerData) {
                      // If customer is selected, clear selection and start new search
                      setSelectedCustomer('');
                      setSelectedCustomerData(null);
                      setCustomerSearchQuery(e.target.value);
                    } else {
                      setCustomerSearchQuery(e.target.value);
                    }
                  }}
                  className={`${dir === 'rtl' ? 'pr-10 text-right' : 'pl-10 text-left'}`}
              disabled={loading}
                  dir={dir}
                  readOnly={!!selectedCustomerData}
                />
                {selectedCustomerData && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedCustomer('');
                      setSelectedCustomerData(null);
                      setCustomerSearchQuery('');
                    }}
                    className={`absolute top-1/2 -translate-y-1/2 h-6 w-6 p-0 ${dir === 'rtl' ? 'left-1' : 'right-1'}`}
                  >
                    ×
                  </Button>
                )}
              </div>

            {/* Search Results */}
            {customerSearchQuery && !selectedCustomerData && (
              <ScrollArea className="h-[200px] border rounded-md">
                <div className="p-2">
                  {customerSearchLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className={`text-sm text-muted-foreground ${dir === 'rtl' ? 'mr-2' : 'ml-2'}`}>
                        {t('dashboard.admin.manualScheduling.searching')}
                      </span>
                    </div>
                  ) : customerSearchResults.length > 0 ? (
                    <div className="space-y-1">
                      {customerSearchResults.map((customer) => (
                        <div
                          key={customer.id}
                          className={`flex items-center justify-between p-2 rounded-md hover:bg-muted cursor-pointer ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          <div className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                            <div className="font-medium text-sm">
                              {language === 'ar' 
                                ? `${customer.firstNameAr || customer.firstName || ''} ${customer.lastNameAr || customer.lastName || ''}`.trim()
                                : `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
                              }
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {customer.phone || customer.email || 'N/A'}
                            </div>
                          </div>
                          <User className="h-4 w-4 text-muted-foreground" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-sm text-muted-foreground">
                      {t('dashboard.admin.manualScheduling.noCustomersFound')}
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </div>

          {!currentCoachId && (
          <div className="space-y-2">
              <Label className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.admin.manualScheduling.coach')}</Label>
            <Select
              value={selectedCoach}
              onValueChange={(value) => {
                setSelectedCoach(value);
                setSelectedSlot('');
              }}
              disabled={loading}
                dir={dir}
              >
                <SelectTrigger className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                  <SelectValue placeholder={t('dashboard.admin.manualScheduling.selectCoach')} />
                </SelectTrigger>
                <SelectContent>
                  {coaches.map((coach) => (
                    <SelectItem key={coach.id} value={coach.id} className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                      {language === 'ar' 
                        ? `${coach.firstNameAr || coach.firstName || ''} ${coach.lastNameAr || coach.lastName || ''}`.trim()
                        : `${coach.firstName || ''} ${coach.lastName || ''}`.trim()
                      } ({coach.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {(() => {
            const shouldShow = !!(selectedCoach || currentCoachId);
            console.log('🎨 [ManualScheduling] Time slots section render check:', {
              selectedCoach,
              currentCoachId,
              shouldShow,
              availableSlotsLength: availableSlots.length,
              loading,
              isDisabled: loading || availableSlots.length === 0
            });
            
            return shouldShow ? (
              <div className="space-y-2">
                <Label className={dir === 'rtl' ? 'text-right' : 'text-left'}>{t('dashboard.admin.manualScheduling.timeSlots')}</Label>
                <Select
                  value={selectedSlot}
                  onValueChange={setSelectedSlot}
                  disabled={loading || availableSlots.length === 0}
                  dir={dir}
                >
                  <SelectTrigger className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                    <SelectValue placeholder={
                      loading ? t('dashboard.admin.manualScheduling.loadingSlots') :
                      availableSlots.length === 0 ? t('dashboard.admin.manualScheduling.noSlots') :
                      t('dashboard.admin.manualScheduling.selectTimeSlot')
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSlots.map((slot, index) => {
                      console.log(`🕐 [ManualScheduling] Rendering slot ${index + 1}:`, slot.date);
                      const formattedDate = language === 'ar' 
                        ? `${format(slot.date, 'PPP', { locale: dateLocale })} الساعة ${format(slot.date, 'h:mm a', { locale: dateLocale })}`
                        : format(slot.date, 'PPP p', { locale: dateLocale });
                      return (
                        <SelectItem
                          key={slot.date.getTime()}
                          value={slot.date.getTime().toString()}
                          className={dir === 'rtl' ? 'text-right' : 'text-left'}
                        >
                          {formattedDate}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            ) : null;
          })()}

          <div className={`flex justify-end gap-2 pt-4 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {t('dashboard.admin.manualScheduling.cancel')}
            </Button>
            <Button
              onClick={handleSchedule}
              disabled={loading || !selectedCustomer || !selectedCoach || !selectedSlot}
            >
              {loading ? (
                <>
                  <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                  {t('dashboard.admin.manualScheduling.scheduling')}
                </>
              ) : (
                t('dashboard.admin.manualScheduling.scheduleAppointment')
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}